/**
 * @author Vibhor <vibhor.sharma@314ecorp.com>
 * @description AgGrid table wrapper
 */
import { useMemo } from 'react';
import _ from 'lodash';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import { Card, Empty, Pagination, Spin, Result } from 'antd';
import { CardProps } from 'antd/lib/card';
import {
	ClientSideRowModelModule,
	ColDef,
	GridReadyEvent,
	ModuleRegistry,
	ValidationModule,
	ColumnAutoSizeModule,
	themeAlpine,
	CellStyleModule,
	RowAutoHeightModule,
	RowSelectionModule,
	RowApiModule,
} from 'ag-grid-community';
import { PaginationProps } from 'antd/lib/pagination';

import { AppButton } from 'components/widgets/button';
import useWindowSize from 'hooks/useWindowSize';
import { VARIABLES } from 'styles/theme';

// Register the module
ModuleRegistry.registerModules([
	ClientSideRowModelModule,
	ValidationModule,
	ColumnAutoSizeModule,
	CellStyleModule,
	RowAutoHeightModule,
	RowSelectionModule,
	RowApiModule,
]);
// ag-grid theme
const gridTheme = themeAlpine.withParams({
	accentColor: VARIABLES.PRIMARY_COLOR,
	backgroundColor: VARIABLES.WHITE_COLOR,
	borderColor: VARIABLES.ICON_HOVER_BG,
	borderRadius: 2,
	browserColorScheme: 'light',
	columnBorder: true,
	columnHoverColor: '#2758CC00',
	foregroundColor: VARIABLES.TEXT_COLOR,
	headerFontSize: 14,
	headerFontWeight: 600,
	headerBackgroundColor: VARIABLES.MODAL_BG,
	headerRowBorder: true,
	iconSize: 14,
	oddRowBackgroundColor: '#F9FAFA',
	rowBorder: true,
	rowHoverColor: '#FFF1CF',
	spacing: 12,
	wrapperBorder: false,
	wrapperBorderRadius: 0,
	headerTextColor: VARIABLES.LABEL_COLOR,
	columnDropCellBackgroundColor: 'red',
	selectedRowBackgroundColor: '#FFF1CF',
});

const ROW_HEIGHT = 46;
const HEADER_HEIGHT = 40;

const REDUCE_PAGE_HEIGHT = {
	invoices: 310,
	auditLogs: 150,
	claimStatusRequests: 275,
	eligibilityBatchRequests: 275,
	eligibilityRequests: 275,
	allWorkLists: 275,
	enrollment: 365,
	selectedPayers: 350,
	providers: 190,
	userManagement: 200,
	selectedOnboardingPayers: 665,
	smallTable: 450,
};

export interface IAgGridTableProps extends AgGridReactProps {
	columnDefs: ColDef[];
	loading?: boolean;
	className?: string;
	defaultColDef?: Record<string, any>;
	gridRef?: React.RefObject<AgGridReact>;
	headerFilter?: CardProps['title'];
	// component in the right side of the header
	headerExtra?: CardProps['extra'];
	paginationProps?: PaginationProps;
	isError?: boolean;
	reload?: VoidFunction;
	// custom
	reduceContainerHeightBy?: number;
}

/**
 * AgGrid table wrapper
 * @param props
 * @returns {React.FC} AgGridTable
 */
const AgGridTable: React.FC<IAgGridTableProps> = (props) => {
	const { windowHeight } = useWindowSize();
	const {
		columnDefs,
		rowData,
		className,
		gridRef,
		defaultColDef,
		headerFilter,
		headerExtra,
		paginationProps,
		isError,
		reload,
		loading,
		reduceContainerHeightBy,
		...restProps
	} = props;

	const onGridReady = (params: GridReadyEvent) => {
		params.api.sizeColumnsToFit();
	};

	const defaultColumnDef = useMemo(() => {
		return {
			resizable: true,
			sortable: false,
			...defaultColDef,
		};
	}, [rowData]);

	const getActions = () => {
		if (paginationProps) {
			return [
				<Pagination
					{...paginationProps}
					key='pagination'
					className='ag-grid-pagination'
					showSizeChanger={true}
					showQuickJumper
					showTotal={(totalRows, range) => `${range[0]}-${range[1]} of ${totalRows} items`}
				/>,
			];
		}
		return null;
	};

	const showErrorMessage = !loading && isError;
	const rowHeight = props.rowHeight || ROW_HEIGHT;
	const headerHeight = props.headerHeight || HEADER_HEIGHT;

	const containerProps: AgGridReactProps = useMemo(() => {
		if (!reduceContainerHeightBy) {
			return { domLayout: 'autoHeight' };
		}
		const availableHeight = windowHeight - (reduceContainerHeightBy || 0);
		const rows = _.size(rowData);
		if (showErrorMessage) {
			return { containerStyle: { height: headerHeight } };
		}
		if (!rows) {
			return { containerStyle: { height: availableHeight } };
		}
		return { containerStyle: { height: _.min([availableHeight, rows * rowHeight + headerHeight + 3]) } };
	}, [showErrorMessage, rowData, loading, rowHeight, headerHeight, windowHeight, reduceContainerHeightBy]);

	return (
		<Card className={className} title={headerFilter} extra={headerExtra} actions={getActions()} rootClassName='ag-grid-card-container'>
			<Spin spinning={!!loading}>
				<AgGridReact
					ref={gridRef}
					theme={gridTheme}
					rowHeight={rowHeight}
					headerHeight={headerHeight}
					loading={false}
					pagination={false}
					animateRows={true}
					suppressMovableColumns={true}
					suppressMenuHide={true}
					suppressContextMenu={true}
					enableCellTextSelection={true}
					rowData={showErrorMessage ? [] : rowData}
					columnDefs={columnDefs}
					onGridReady={onGridReady}
					defaultColDef={defaultColumnDef}
					noRowsOverlayComponent={Empty}
					suppressNoRowsOverlay={isError || loading}
					{...containerProps}
					{...restProps}
				/>
				{showErrorMessage && (
					<Result
						status='warning'
						title='Oops!'
						subTitle='Something went wrong! Please try after sometime.'
						extra={[<AppButton.Reload key='reload' onClick={reload} />]}
					/>
				)}
			</Spin>
		</Card>
	);
};

export { REDUCE_PAGE_HEIGHT };
export default AgGridTable;
